import React from 'react';
import { Link } from 'react-router-dom';
import PublicLayout from './public/PublicLayout';

const PlaceholderPage = ({ title, description, user, onLogout, currentPath = '', children }) => {
  return (
    <PublicLayout>
      <div className="bg-light py-4">
        <div className="container">
        {children ? (
          <>
            <div className="mb-4">
              <h1 className="display-6 fw-bold text-dark">{title}</h1>
              <p className="lead text-muted">{description}</p>
            </div>
            {children}
          </>
        ) : (
          <div className="text-center py-5">
            <div className="mx-auto mb-4" style={{ width: '96px', height: '96px', backgroundColor: '#dbeafe', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <svg style={{ width: '48px', height: '48px', color: '#2563eb' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 14l-7 7m0 0l-7-7m7 7V3"></path>
              </svg>
            </div>
            <h1 className="display-4 fw-bold text-dark mb-3">{title}</h1>
            <p className="lead text-muted mb-4">{description}</p>
            <div className="mb-4">
              <p className="fs-5 text-primary fw-medium">🚧 Coming Soon!</p>
              <p className="text-muted">This feature is under development and will be available soon.</p>
              <Link 
                to="/" 
                className="btn btn-primary btn-lg mt-3"
              >
                Back to Home
              </Link>
            </div>
          </div>
        )}
        </div>
      </div>
    </PublicLayout>
  );
};

// LMS Components
export const LMSPage = ({ user, onLogout }) => {
  const courses = [
    {
      id: 1,
      title: "Fundamentals of Respiratory Care",
      price: "$299",
      duration: "8 weeks",
      level: "Beginner",
      description: "Introduction to basic respiratory care principles and techniques.",
      instructor: "Dr. Sarah Johnson"
    },
    {
      id: 2,
      title: "Advanced Mechanical Ventilation",
      price: "$499",
      duration: "12 weeks",
      level: "Advanced",
      description: "Comprehensive course on mechanical ventilation strategies and troubleshooting.",
      instructor: "Prof. Michael Chen"
    },
    {
      id: 3,
      title: "Pediatric Respiratory Care",
      price: "$399",
      duration: "10 weeks",
      level: "Intermediate",
      description: "Specialized care for pediatric respiratory conditions and treatments.",
      instructor: "Dr. Amina Hassan"
    },
    {
      id: 4,
      title: "Respiratory Therapy Assessment",
      price: "$249",
      duration: "6 weeks",
      level: "Beginner",
      description: "Learn comprehensive patient assessment techniques for respiratory therapy.",
      instructor: "Dr. James Wilson"
    },
    {
      id: 5,
      title: "Critical Care Respiratory Management",
      price: "$599",
      duration: "14 weeks",
      level: "Advanced",
      description: "Advanced respiratory management in critical care settings.",
      instructor: "Dr. Lisa Thompson"
    },
    {
      id: 6,
      title: "Home Care Respiratory Therapy",
      price: "$349",
      duration: "8 weeks",
      level: "Intermediate",
      description: "Respiratory care management in home healthcare settings.",
      instructor: "Dr. Robert Davis"
    }
  ];

  return (
    <PlaceholderPage 
      title="ARCA Virtual University" 
      description="Advance your respiratory therapy career with our comprehensive online courses."
      user={user}
      onLogout={onLogout}
      currentPath="/courses"
    >
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mt-8">
        {courses.map((course) => (
          <div key={course.id} className="bg-white rounded-lg shadow-md overflow-hidden hover:shadow-lg transition-shadow">
            <div className="h-48 bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center">
              <span className="text-white text-xl font-bold">RT Course</span>
            </div>
            <div className="p-6">
              <div className="flex justify-between items-start mb-2">
                <h3 className="text-lg font-semibold text-gray-900 line-clamp-2">{course.title}</h3>
                <span className="text-2xl font-bold text-green-600">{course.price}</span>
              </div>
              <p className="text-gray-600 text-sm mb-4 line-clamp-2">{course.description}</p>
              <div className="space-y-2 text-sm text-gray-500 mb-4">
                <div className="flex justify-between">
                  <span>Duration:</span>
                  <span>{course.duration}</span>
                </div>
                <div className="flex justify-between">
                  <span>Level:</span>
                  <span className={`px-2 py-1 rounded-full text-xs ${
                    course.level === 'Beginner' ? 'bg-green-100 text-green-800' :
                    course.level === 'Intermediate' ? 'bg-yellow-100 text-yellow-800' :
                    'bg-red-100 text-red-800'
                  }`}>{course.level}</span>
                </div>
                <div className="flex justify-between">
                  <span>Instructor:</span>
                  <span>{course.instructor}</span>
                </div>
              </div>
              <button className="w-full bg-blue-500 hover:bg-blue-600 text-white py-2 px-4 rounded-md font-medium transition-colors">
                Enroll Now
              </button>
            </div>
          </div>
        ))}
      </div>
    </PlaceholderPage>
  );
};

export const CoursePage = ({ user, onLogout }) => (
  <PlaceholderPage 
    title="Course Details" 
    description="View course content, lessons, and track your learning progress."
    user={user}
    onLogout={onLogout}
    currentPath="/courses"
  />
);

export const CoursePlayer = ({ user, onLogout }) => (
  <PlaceholderPage 
    title="Course Player" 
    description="Interactive course content and learning materials."
    user={user}
    onLogout={onLogout}
    currentPath="/courses"
  />
);

// Store Components
export const StorePage = ({ user, onLogout }) => {
  const storeItems = [
    {
      id: 1,
      name: "Professional Stethoscope - Cardiology III",
      price: 299.99,
      originalPrice: 349.99,
      category: "Equipment",
      rating: 4.8,
      reviews: 124,
      description: "High-quality cardiology stethoscope designed for respiratory assessment and cardiac auscultation.",
      inStock: true,
      badge: "Best Seller"
    },
    {
      id: 2,
      name: "Respiratory Therapy Textbook (6th Edition)",
      price: 89.99,
      originalPrice: 109.99,
      category: "Books",
      rating: 4.6,
      reviews: 89,
      description: "Comprehensive guide to respiratory therapy principles, practice, and latest clinical techniques.",
      inStock: true,
      badge: "New Edition"
    },
    {
      id: 3,
      name: "Pulse Oximeter - Professional Grade",
      price: 159.99,
      originalPrice: 189.99,
      category: "Equipment",
      rating: 4.9,
      reviews: 203,
      description: "Accurate pulse oximetry device for continuous patient monitoring with advanced sensors.",
      inStock: true,
      badge: "Top Rated"
    },
    {
      id: 4,
      name: "ARCA Branded Medical Scrubs Set",
      price: 49.99,
      originalPrice: 59.99,
      category: "Apparel",
      rating: 4.4,
      reviews: 67,
      description: "Comfortable and professional ARCA branded medical scrubs made from premium fabric.",
      inStock: true,
      badge: "Member Exclusive"
    },
    {
      id: 5,
      name: "Ventilator Circuit Kit - Complete Set",
      price: 79.99,
      originalPrice: 95.99,
      category: "Equipment",
      rating: 4.7,
      reviews: 156,
      description: "Complete ventilator circuit kit with all necessary components for mechanical ventilation.",
      inStock: false,
      badge: "Out of Stock"
    },
    {
      id: 6,
      name: "RT Clinical Procedures Handbook",
      price: 39.99,
      originalPrice: 49.99,
      category: "Books",
      rating: 4.5,
      reviews: 92,
      description: "Quick reference guide for respiratory therapy procedures and clinical protocols.",
      inStock: true,
      badge: "Essential"
    },
    {
      id: 7,
      name: "Nebulizer Kit - Professional Series",
      price: 129.99,
      originalPrice: 149.99,
      category: "Equipment",
      rating: 4.8,
      reviews: 178,
      description: "High-quality nebulizer system for effective medication delivery to patients.",
      inStock: true,
      badge: "Professional Grade"
    },
    {
      id: 8,
      name: "ARCA Annual Conference Bag",
      price: 24.99,
      originalPrice: 29.99,
      category: "Accessories",
      rating: 4.3,
      reviews: 45,
      description: "Official ARCA conference bag with multiple compartments for professionals.",
      inStock: true,
      badge: "Limited Edition"
    }
  ];

  const categories = ["All Items", "Equipment", "Books", "Apparel", "Accessories"];

  return (
    <PlaceholderPage 
      title="ARCA Store" 
      description="Shop for respiratory therapy equipment, textbooks, and professional resources."
      user={user}
      onLogout={onLogout}
      currentPath="/store"
    >
      {/* Filter and Sort Bar */}
      <div className="mb-6 bg-white rounded-lg shadow p-4">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center space-y-4 md:space-y-0">
          <div className="flex flex-wrap gap-2">
            {categories.map((category, index) => (
              <button 
                key={category}
                className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                  index === 0 
                    ? 'bg-blue-500 text-white' 
                    : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                }`}
              >
                {category}
              </button>
            ))}
          </div>
          <div className="flex items-center space-x-2">
            <span className="text-gray-600 text-sm">Sort by:</span>
            <select className="border border-gray-300 rounded-md px-3 py-2 text-sm">
              <option>Price: Low to High</option>
              <option>Price: High to Low</option>
              <option>Most Popular</option>
              <option>Highest Rated</option>
              <option>Newest First</option>
            </select>
          </div>
        </div>
      </div>
      
      {/* Products Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
        {storeItems.map((item) => (
          <div key={item.id} className="bg-white rounded-lg shadow-md overflow-hidden hover:shadow-lg transition-shadow">
            {/* Product Image */}
            <div className="relative h-48 bg-gradient-to-br from-blue-100 to-purple-100 flex items-center justify-center">
              <div className="text-center">
                <svg className="w-16 h-16 text-gray-400 mx-auto mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
                </svg>
                <span className="text-gray-500 text-sm">Product Image</span>
              </div>
              {item.badge && (
                <span className={`absolute top-2 left-2 px-2 py-1 text-xs font-semibold rounded-full ${
                  item.badge === 'Best Seller' ? 'bg-yellow-100 text-yellow-800' :
                  item.badge === 'New Edition' ? 'bg-green-100 text-green-800' :
                  item.badge === 'Top Rated' ? 'bg-blue-100 text-blue-800' :
                  item.badge === 'Out of Stock' ? 'bg-red-100 text-red-800' :
                  'bg-purple-100 text-purple-800'
                }`}>
                  {item.badge}
                </span>
              )}
            </div>
            
            {/* Product Info */}
            <div className="p-4">
              <h3 className="text-lg font-semibold text-gray-900 mb-2 line-clamp-2">{item.name}</h3>
              <p className="text-gray-600 text-sm mb-3 line-clamp-2">{item.description}</p>
              
              {/* Rating */}
              <div className="flex items-center mb-3">
                <div className="flex items-center">
                  {[...Array(5)].map((_, i) => (
                    <svg key={i} className={`w-4 h-4 ${i < Math.floor(item.rating) ? 'text-yellow-400' : 'text-gray-300'}`} fill="currentColor" viewBox="0 0 20 20">
                      <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                    </svg>
                  ))}
                  <span className="text-sm text-gray-600 ml-1">{item.rating} ({item.reviews})</span>
                </div>
              </div>
              
              {/* Price and Action */}
              <div className="flex justify-between items-center">
                <div className="flex items-center space-x-2">
                  <span className="text-2xl font-bold text-green-600">${item.price}</span>
                  {item.originalPrice > item.price && (
                    <span className="text-sm text-gray-500 line-through">${item.originalPrice}</span>
                  )}
                </div>
                <button 
                  className={`px-4 py-2 rounded-md font-medium text-sm transition-colors ${
                    item.inStock 
                      ? 'bg-blue-500 hover:bg-blue-600 text-white' 
                      : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                  }`}
                  disabled={!item.inStock}
                >
                  {item.inStock ? 'Add to Cart' : 'Out of Stock'}
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>
      
      {/* Load More Button */}
      <div className="text-center mt-8">
        <button className="bg-blue-500 hover:bg-blue-600 text-white px-6 py-3 rounded-md font-medium">
          Load More Products
        </button>
      </div>
    </PlaceholderPage>
  );
};

export const ProductDetails = ({ user, onLogout }) => (
  <PlaceholderPage 
    title="Product Details" 
    description="Detailed product information, specifications, and customer reviews."
    user={user}
    onLogout={onLogout}
    currentPath="/store"
  />
);

export const ShoppingCart = ({ user, onLogout }) => (
  <PlaceholderPage 
    title="Shopping Cart" 
    description="Review your selected items and proceed to checkout."
    user={user}
    onLogout={onLogout}
    currentPath="/cart"
  />
);

export const Checkout = ({ user, onLogout }) => (
  <PlaceholderPage 
    title="Checkout" 
    description="Complete your purchase with secure payment processing."
    user={user}
    onLogout={onLogout}
    currentPath="/checkout"
  />
);

// Community Components
export const CommunityPage = ({ user, onLogout }) => {
  const forumTopics = [
    {
      id: 1,
      title: "Best Practices in Mechanical Ventilation for COVID-19 Patients",
      author: "Dr. Sarah Johnson",
      authorTitle: "Senior Respiratory Therapist",
      country: "Nigeria",
      replies: 24,
      views: 156,
      lastActivity: "2 hours ago",
      category: "Clinical Practice",
      isPinned: true,
      hasNewReplies: true
    },
    {
      id: 2,
      title: "Respiratory Therapy Education Standards Across African Countries",
      author: "Prof. Michael Chen",
      authorTitle: "Director of RT Education",
      country: "South Africa",
      replies: 18,
      views: 89,
      lastActivity: "5 hours ago",
      category: "Education",
      isPinned: false,
      hasNewReplies: true
    },
    {
      id: 3,
      title: "Latest Research: Non-invasive Ventilation in Pediatric Care",
      author: "Dr. Amina Hassan",
      authorTitle: "Pediatric RT Specialist",
      country: "Kenya",
      replies: 32,
      views: 203,
      lastActivity: "1 day ago",
      category: "Research",
      isPinned: false,
      hasNewReplies: false
    },
    {
      id: 4,
      title: "Equipment Maintenance in Resource-Limited Settings",
      author: "James Okafor",
      authorTitle: "Biomedical Technician",
      country: "Ghana",
      replies: 15,
      views: 67,
      lastActivity: "2 days ago",
      category: "Equipment",
      isPinned: false,
      hasNewReplies: true
    },
    {
      id: 5,
      title: "Building RT Programs in Rural Hospitals",
      author: "Dr. Fatima Al-Rashid",
      authorTitle: "Hospital Administrator",
      country: "Morocco",
      replies: 28,
      views: 134,
      lastActivity: "3 days ago",
      category: "Administration",
      isPinned: false,
      hasNewReplies: false
    }
  ];

  const events = [
    {
      id: 1,
      title: "Monthly Webinar: Pediatric Respiratory Care",
      date: "2024-12-20",
      time: "14:00 GMT",
      type: "Webinar",
      attendees: 234
    },
    {
      id: 2,
      title: "ARCA Regional Conference - West Africa",
      date: "2024-12-28",
      time: "09:00 GMT",
      type: "Conference",
      attendees: 567
    },
    {
      id: 3,
      title: "Equipment Training Workshop",
      date: "2025-01-15",
      time: "10:00 GMT",
      type: "Workshop",
      attendees: 89
    }
  ];

  const memberSpotlight = [
    {
      id: 1,
      name: "Dr. Kwame Asante",
      title: "Chief Respiratory Therapist",
      country: "Ghana",
      experience: "15 years",
      specialization: "Critical Care",
      contributions: "Published 23 research papers"
    },
    {
      id: 2,
      name: "Aisha Mohammed",
      title: "RT Program Director",
      country: "Nigeria",
      experience: "12 years",
      specialization: "Education",
      contributions: "Trained 200+ respiratory therapists"
    }
  ];

  return (
    <PlaceholderPage 
      title="ARCA Community" 
      description="Connect with fellow respiratory care professionals across Africa, share knowledge, and collaborate."
      user={user}
      onLogout={onLogout}
      currentPath="/community"
    >
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Main Content */}
        <div className="lg:col-span-2 space-y-6">
          {/* Quick Actions */}
          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Quick Actions</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <button className="p-4 border border-gray-200 rounded-lg hover:bg-gray-50 text-center transition-colors">
                <svg className="h-8 w-8 text-blue-600 mx-auto mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                </svg>
                <p className="text-sm font-medium">New Topic</p>
              </button>
              <button className="p-4 border border-gray-200 rounded-lg hover:bg-gray-50 text-center transition-colors">
                <svg className="h-8 w-8 text-green-600 mx-auto mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 8h2a2 2 0 012 2v6a2 2 0 01-2 2h-2v4l-4-4H9a1.994 1.994 0 01-1.414-.586m0 0L11 14h4a2 2 0 002-2V6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2v4l.586-.586z" />
                </svg>
                <p className="text-sm font-medium">Messages</p>
              </button>
              <button className="p-4 border border-gray-200 rounded-lg hover:bg-gray-50 text-center transition-colors">
                <svg className="h-8 w-8 text-purple-600 mx-auto mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.5 2.5 0 11-5 0 2.5 2.5 0 015 0z" />
                </svg>
                <p className="text-sm font-medium">Find Members</p>
              </button>
              <button className="p-4 border border-gray-200 rounded-lg hover:bg-gray-50 text-center transition-colors">
                <svg className="h-8 w-8 text-orange-600 mx-auto mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
                <p className="text-sm font-medium">Events</p>
              </button>
            </div>
          </div>

          {/* Forum Topics */}
          <div className="bg-white rounded-lg shadow">
            <div className="p-6 border-b border-gray-200">
              <div className="flex justify-between items-center">
                <h3 className="text-xl font-semibold text-gray-900">Recent Forum Discussions</h3>
                <button className="text-blue-600 hover:text-blue-800 text-sm font-medium">View All</button>
              </div>
            </div>
            <div className="divide-y divide-gray-200">
              {forumTopics.map((topic) => (
                <div key={topic.id} className="p-6 hover:bg-gray-50 cursor-pointer transition-colors">
                  <div className="flex items-start space-x-4">
                    {/* Avatar */}
                    <div className="flex-shrink-0">
                      <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center">
                        <span className="text-white font-semibold text-sm">
                          {topic.author.split(' ').map(n => n[0]).join('')}
                        </span>
                      </div>
                    </div>
                    
                    {/* Content */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center space-x-2 mb-1">
                        {topic.isPinned && (
                          <svg className="w-4 h-4 text-red-500" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M3 3a1 1 0 000 2v8a2 2 0 002 2h2.586l-1.293 1.293a1 1 0 101.414 1.414L10 15.414l2.293 2.293a1 1 0 001.414-1.414L12.414 15H15a2 2 0 002-2V5a1 1 0 100-2H3zm11.707 4.707a1 1 0 00-1.414-1.414L10 9.586 8.707 8.293a1 1 0 00-1.414 0l-2 2a1 1 0 001.414 1.414L8 10.414l1.293 1.293a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                          </svg>
                        )}
                        <h4 className="text-lg font-medium text-blue-600 hover:text-blue-800">
                          {topic.title}
                        </h4>
                        {topic.hasNewReplies && (
                          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                            New
                          </span>
                        )}
                      </div>
                      
                      <div className="flex items-center space-x-4 text-sm text-gray-500 mb-2">
                        <span className="font-medium">{topic.author}</span>
                        <span>•</span>
                        <span>{topic.authorTitle}</span>
                        <span>•</span>
                        <span>{topic.country}</span>
                        <span className="text-xs bg-gray-100 text-gray-600 px-2 py-1 rounded-full">
                          {topic.category}
                        </span>
                      </div>
                      
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-4 text-sm text-gray-500">
                          <span>{topic.replies} replies</span>
                          <span>{topic.views} views</span>
                          <span>{topic.lastActivity}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Member Spotlight */}
          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Member Spotlight</h3>
            <div className="space-y-4">
              {memberSpotlight.map((member) => (
                <div key={member.id} className="border border-gray-200 rounded-lg p-4">
                  <div className="flex items-center space-x-3 mb-3">
                    <div className="w-12 h-12 bg-gradient-to-br from-green-500 to-blue-600 rounded-full flex items-center justify-center">
                      <span className="text-white font-semibold">
                        {member.name.split(' ').map(n => n[0]).join('')}
                      </span>
                    </div>
                    <div>
                      <h4 className="font-semibold text-gray-900">{member.name}</h4>
                      <p className="text-sm text-gray-600">{member.title}</p>
                    </div>
                  </div>
                  <div className="space-y-1 text-sm text-gray-600">
                    <p><span className="font-medium">Country:</span> {member.country}</p>
                    <p><span className="font-medium">Experience:</span> {member.experience}</p>
                    <p><span className="font-medium">Specialty:</span> {member.specialization}</p>
                    <p className="text-xs text-blue-600">{member.contributions}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Upcoming Events */}
          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Upcoming Community Events</h3>
            <div className="space-y-3">
              {events.map((event) => (
                <div key={event.id} className="border-l-4 border-green-500 pl-4">
                  <h4 className="font-medium text-gray-900 mb-1">{event.title}</h4>
                  <div className="text-sm text-gray-600 space-y-1">
                    <p>{event.date} at {event.time}</p>
                    <div className="flex items-center justify-between">
                      <span className={`text-xs px-2 py-1 rounded-full ${
                        event.type === 'Webinar' ? 'bg-blue-100 text-blue-800' :
                        event.type === 'Conference' ? 'bg-purple-100 text-purple-800' :
                        'bg-green-100 text-green-800'
                      }`}>
                        {event.type}
                      </span>
                      <span className="text-xs text-gray-500">{event.attendees} attending</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
            <button className="mt-4 w-full bg-blue-500 hover:bg-blue-600 text-white py-2 px-4 rounded-md text-sm font-medium">
              View All Events
            </button>
          </div>

          {/* Community Stats */}
          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Community Stats</h3>
            <div className="space-y-3">
              <div className="flex justify-between">
                <span className="text-gray-600">Total Members:</span>
                <span className="font-semibold">1,247</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Active Today:</span>
                <span className="font-semibold text-green-600">89</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Forum Posts:</span>
                <span className="font-semibold">3,521</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Countries:</span>
                <span className="font-semibold">32</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Online Now:</span>
                <span className="font-semibold text-blue-600">156</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </PlaceholderPage>
  );
};

export const ForumPage = ({ user, onLogout }) => (
  <PlaceholderPage 
    title="Discussion Forums" 
    description="Participate in professional discussions and knowledge sharing."
    user={user}
    onLogout={onLogout}
    currentPath="/community"
  />
);

export const MessagingPage = ({ user, onLogout }) => (
  <PlaceholderPage 
    title="Messages" 
    description="Private messaging with other ARCA members and professionals."
    user={user}
    onLogout={onLogout}
    currentPath="/community"
  />
);

// CPD Components
export const CPDPage = ({ user, onLogout }) => (
  <PlaceholderPage 
    title="Continuing Professional Development" 
    description="Track your CPD activities, points, and maintain your professional certification."
    user={user}
    onLogout={onLogout}
    currentPath="/cpd"
  />
);

export const CPDTracker = ({ user, onLogout }) => (
  <PlaceholderPage 
    title="CPD Tracker" 
    description="Monitor your CPD progress and view detailed activity reports."
    user={user}
    onLogout={onLogout}
    currentPath="/cpd"
  />
);

// Resources Components
export const ResourcesPage = ({ user, onLogout }) => (
  <PlaceholderPage 
    title="Resources Library" 
    description="Access clinical guidelines, research papers, and educational materials."
    user={user}
    onLogout={onLogout}
    currentPath="/resources"
  />
);

export const KnowledgeHub = ({ user, onLogout }) => (
  <PlaceholderPage 
    title="Knowledge Hub" 
    description="Comprehensive repository of respiratory care knowledge and best practices."
    user={user}
    onLogout={onLogout}
    currentPath="/knowledge-hub"
  />
);

// Profile and Settings Components
export const ProfilePage = ({ user, onLogout }) => (
  <PlaceholderPage 
    title="My Profile" 
    description="Manage your personal information, qualifications, and professional details."
    user={user}
    onLogout={onLogout}
    currentPath="/profile"
  />
);

export const MembershipPage = ({ user, onLogout }) => (
  <PlaceholderPage 
    title="Membership Management" 
    description="View your membership status, benefits, and renewal options."
    user={user}
    onLogout={onLogout}
    currentPath="/membership"
  />
);

export const PaymentsPage = ({ user, onLogout }) => (
  <PlaceholderPage 
    title="Payment History" 
    description="View your payment history, invoices, and manage billing information."
    user={user}
    onLogout={onLogout}
    currentPath="/payments"
  />
);

export const SettingsPage = ({ user, onLogout }) => (
  <PlaceholderPage 
    title="Account Settings" 
    description="Manage your account preferences, notifications, and privacy settings."
    user={user}
    onLogout={onLogout}
    currentPath="/settings"
  />
);

// Admin Components
export const AdminMembers = ({ user, onLogout }) => (
  <PlaceholderPage 
    title="Member Management" 
    description="Manage member registrations, approvals, and account status."
    user={user}
    onLogout={onLogout}
    currentPath="/admin"
  />
);

export const AdminEvents = ({ user, onLogout }) => (
  <PlaceholderPage 
    title="Event Management" 
    description="Create, edit, and manage events and conferences."
    user={user}
    onLogout={onLogout}
    currentPath="/admin"
  />
);

export const AdminStore = ({ user, onLogout }) => (
  <PlaceholderPage 
    title="Store Management" 
    description="Manage products, inventory, and orders in the ARCA store."
    user={user}
    onLogout={onLogout}
    currentPath="/admin"
  />
);

export const AdminCourses = ({ user, onLogout }) => (
  <PlaceholderPage 
    title="Course Management" 
    description="Create and manage LMS courses and educational content."
    user={user}
    onLogout={onLogout}
    currentPath="/admin"
  />
);

export const AdminReports = ({ user, onLogout }) => (
  <PlaceholderPage 
    title="Reports & Analytics" 
    description="View platform analytics, member activity, and generate reports."
    user={user}
    onLogout={onLogout}
    currentPath="/admin"
  />
);

export const AdminSettings = ({ user, onLogout }) => (
  <PlaceholderPage 
    title="System Settings" 
    description="Configure platform settings, integrations, and system parameters."
    user={user}
    onLogout={onLogout}
    currentPath="/admin"
  />
);

export default PlaceholderPage;